import { createContext, useContext, useState, type ReactNode } from 'react'
import { DEFAULT_THEME, STORAGE_KEYS, THEMES, type Theme } from '@/constants'

interface ThemeContextValue {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined)

function isValidTheme(value: string): value is Theme {
  return (THEMES as readonly string[]).includes(value)
}

function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme)
}

function resolveInitialTheme(): Theme {
  const stored = localStorage.getItem(STORAGE_KEYS.THEME)
  if (stored && isValidTheme(stored)) return stored

  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
  return prefersDark ? 'dark' : DEFAULT_THEME
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    const initial = resolveInitialTheme()
    applyTheme(initial)
    return initial
  })

  function setTheme(next: Theme): void {
    setThemeState(next)
    applyTheme(next)
    localStorage.setItem(STORAGE_KEYS.THEME, next)
  }

  return <ThemeContext.Provider value={{ theme, setTheme }}>{children}</ThemeContext.Provider>
}

export function useThemeContext(): ThemeContextValue {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useThemeContext must be used within a ThemeProvider')
  }
  return context
}
